import { useTranslation } from 'react-i18next'
import { Text } from '@mantine/core'
import { usePasswordStrength } from 'auth/hooks'

interface PasswordStrengthIndicatorProps {
  password: string
}

export default function PasswordStrengthIndicator({
  password,
}: PasswordStrengthIndicatorProps) {
  const { t } = useTranslation()
  const strength = usePasswordStrength(password)

  const getTextColor = (isValid: boolean) => {
    return isValid
      ? 'text-[var(--mantine-color-green-6)]'
      : 'text-[var(--mantine-color-gray-6)]'
  }

  const requirements = [
    {
      key: 'minLength',
      isValid: strength.hasMinLength,
      text: t('auth.passwordRequirements.minLength', 'Minimum 8 znaków'),
    },
    {
      key: 'specialChar',
      isValid: strength.hasSpecialChar,
      text:
        t('auth.passwordRequirements.specialChar', 'Minimum jeden znak specjalny') +
        ': !@#$%^&*',
    },
    {
      key: 'number',
      isValid: strength.hasNumber,
      text: t('auth.passwordRequirements.number', 'Minimum jedna cyfra'),
    },
    {
      key: 'upperLower',
      isValid: strength.hasUpperCase && strength.hasLowerCase,
      text: t('auth.passwordRequirements.upperLower', 'Małe i duże litery'),
    },
  ]

  if (!password) {
    return null
  }

  return (
    <div className={'mt-2 space-y-1'}>
      {requirements.map((requirement) => (
        <Text
          className={`${getTextColor(requirement.isValid)} flex items-center text-xs`}
          key={requirement.key}
          size={'xs'}
        >
          <span
            className={`mr-2 ${requirement.isValid ? 'text-[var(--mantine-color-green-6)]' : 'text-[var(--mantine-color-gray-6)]'}`}
          >
            {requirement.isValid ? '✓' : '○'}
          </span>
          {requirement.text}
        </Text>
      ))}
    </div>
  )
}
